import React from 'react';
import { Typography, theme } from 'antd';
import type { FtueFunnelStep, FtueProgressionView } from '@/types';
import { dayLabel, fmtNum, fmtPct } from '../bq-insights/bqMath';
import { fmtMinutes, tooFew } from './ftueFormat';

const { Text } = Typography;

/** A step losing this share or more of the step before it is shown in red. */
const HOT_LOSS = 0.1;

const COLUMNS = 7;

/** The 3 steps that lose the largest share of the step before, ties by players lost. */
function largestDrops(funnel: FtueFunnelStep[]) {
  return funnel
    .slice(1)
    .map((s, i) => ({ to: s, from: funnel[i], lost: funnel[i].players - s.players }))
    .filter((d) => d.to.lostHere !== null && d.to.lostHere > 0)
    .sort((a, b) => b.to.lostHere! - a.to.lostHere! || b.lost - a.lost)
    .slice(0, 3);
}

export interface FtueFunnelTableProps {
  view: FtueProgressionView;
  minPlayers: number;
}

/** Day-0 tutorial funnel of one view: notes, the three largest drops, then one row per step. */
export const FtueFunnelTable: React.FC<FtueFunnelTableProps> = ({ view, minPlayers }) => {
  const { token } = theme.useToken();
  const { funnel, installs, loginTrouble, partialFrom } = view;
  const few = tooFew(installs, minPlayers);
  const drops = largestDrops(funnel);
  const cell: React.CSSProperties = {
    padding: '4px 6px',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  };
  const head: React.CSSProperties = {
    ...cell,
    color: token.colorTextSecondary,
    fontWeight: 500,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  };

  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: 12 }}>
        <Text type="secondary">{`n = ${fmtNum(installs)} installs`}</Text>
        {few && (
          <Text type="warning" style={{ marginLeft: 8 }}>
            too few players
          </Text>
        )}
      </div>
      {partialFrom !== null && (
        <div style={{ marginBottom: 8 }}>
          <Text type="warning">
            {`Installs from ${dayLabel(partialFrom)} have had less than 24 hours, so later steps are still filling.`}
          </Text>
        </div>
      )}
      <div style={few ? { opacity: 0.5 } : undefined}>
        {drops.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ fontSize: 12 }}>
              Largest drops
            </Text>
            {drops.map((d) => (
              <div key={d.to.step} data-drop={d.to.step} style={{ fontSize: 12 }}>
                <Text type="danger">{`−${fmtPct(d.to.lostHere)}`}</Text>{' '}
                <Text type="secondary">
                  {`${d.from.label} → ${d.to.label} (${fmtNum(d.lost)} players)`}
                </Text>
              </div>
            ))}
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>Step</th>
                <th style={{ ...head, width: '30%' }} />
                <th style={head}>Players</th>
                <th style={head}>% of installs</th>
                <th style={head}>Lost here</th>
                <th style={head}>Median time since install</th>
                <th style={{ ...head, fontWeight: 400 }}>Logged</th>
              </tr>
            </thead>
            <tbody>
              {funnel.map((s) => {
                const hot = s.lostHere !== null && s.lostHere >= HOT_LOSS;
                return (
                  <React.Fragment key={s.step}>
                    <tr
                      data-step={s.step}
                      className={hot ? 'ftue-hot' : undefined}
                      style={hot ? { color: token.colorError } : undefined}
                    >
                      <td style={{ ...cell, textAlign: 'left', color: token.colorText }}>
                        {s.label}
                      </td>
                      <td style={{ ...cell, textAlign: 'left' }}>
                        <div
                          style={{
                            height: 12,
                            borderRadius: 3,
                            background: token.colorPrimary,
                            opacity: 0.8,
                            width: `${Math.min(100, (s.shareOfInstalls ?? 0) * 100)}%`,
                          }}
                        />
                      </td>
                      <td style={cell}>{fmtNum(s.players)}</td>
                      <td style={cell}>{fmtPct(s.shareOfInstalls)}</td>
                      <td style={{ ...cell, fontWeight: hot ? 600 : undefined }}>
                        {s.lostHere === null ? '—' : `−${fmtPct(s.lostHere)}`}
                      </td>
                      <td style={cell}>{fmtMinutes(s.medianMinutes)}</td>
                      <td style={{ ...cell, color: token.colorTextTertiary }}>
                        {fmtNum(s.logged)}
                      </td>
                    </tr>
                    {s.step === 1 && loginTrouble.failed > 0 && (
                      <tr data-note="login-trouble">
                        <td colSpan={COLUMNS} style={{ ...cell, textAlign: 'left', fontSize: 12 }}>
                          <Text type="secondary">
                            {`${fmtNum(loginTrouble.failed)} players hit a Google login failure; ${fmtNum(loginTrouble.neverLoggedIn)} of them never got in.`}
                          </Text>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
